import { useMemo } from 'react';

import { useActiveClass } from 'src/class/class-provider';

import { usePacing } from 'src/sections/curriculum/pacing/use-pacing';
import { useCoursePacingTarget } from 'src/sections/curriculum/pacing/use-course-pacing-target';

// When the active class taught each lesson (epic #318 make-up lessons), from the same
// pacing the Pacing tab / weekly view use (per-class overrides, excluded and half days
// included). Drives "taught on Oct 8" on the lesson page and the roster's "absent on
// these dates → these lessons" suggestion. There is no attendance data — pacing is the
// schedule of record.

export function useClassLessonDates() {
  const { activeClass } = useActiveClass();
  const target = useCoursePacingTarget(activeClass?.course.id ?? null);
  const { pacing, lessonTitle, isPending } = usePacing(activeClass ?? null, target.pct / 100);

  return useMemo(() => {
    const dateByLesson = new Map<string, string>();
    const lessonsByDate = new Map<string, string[]>();
    for (const unit of pacing?.units ?? []) {
      for (const [lessonId, date] of Object.entries(unit.lessonDates)) {
        dateByLesson.set(lessonId, date);
        lessonsByDate.set(date, [...(lessonsByDate.get(date) ?? []), lessonId]);
      }
    }
    return { dateByLesson, lessonsByDate, lessonTitle, isPending: isPending || !target.isLoaded };
  }, [pacing, lessonTitle, isPending, target.isLoaded]);
}

/** "2026-10-08" → "Thu, Oct 8" (calendar date — no time-zone shift). */
export function formatLessonDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Today as YYYY-MM-DD in the viewer's time zone. */
export function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}
